import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import {
  Bot,
  MessageSquare,
  Clapperboard,
  LogOut,
  Menu,
  X,
  Globe,
  Newspaper,
  Zap,
  User,
  LayoutDashboard,
  ChevronRight,
  PinIcon,
} from "lucide-react";

type NavItem = {
  title: string;
  subtitle: string;
  path: string;
  icon: React.ElementType;
  accent: string;
  lightBg: string;
  activeBg: string;
  activeText: string;
  activeBorder: string;
};

const SIDEBAR_ITEMS: NavItem[] = [
  {
    title: "Radha's Input",
    subtitle: "CEO input workflow",
    path: "/radhai-admin/voice-clone",
    icon: MessageSquare,
    accent: "#0369a1",
    lightBg: "#e0f2fe",
    activeBg: "#dbeafe",
    activeText: "#1d4ed8",
    activeBorder: "#3b82f6",
  },
  {
    title: "R&D Video Upload",
    subtitle: "Video reasoning flow",
    path: "/radhai-admin/research-development",
    icon: Clapperboard,
    accent: "#15803d",
    lightBg: "#dcfce7",
    activeBg: "#dcfce7",
    activeText: "#166534",
    activeBorder: "#22c55e",
  },
  {
    title: "Paper Clipping AI",
    subtitle: "Analyze paper clips",
    path: "/radhai-admin/paper-clipping",
    icon: Newspaper,
    accent: "#6d28d9",
    lightBg: "#ede9fe",
    activeBg: "#ede9fe",
    activeText: "#5b21b6",
    activeBorder: "#8b5cf6",
  },
  {
    title: "Google Workspace",
    subtitle: "Coming soon",
    path: "/radhai-admin/my-google",
    icon: Globe,
    accent: "#c2410c",
    lightBg: "#ffedd5",
    activeBg: "#ffedd5",
    activeText: "#9a3412",
    activeBorder: "#f97316",
  },
];

export default function SidebarLayout({ onLogout }: { onLogout: () => void }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [hovered, setHovered] = useState(false);
  const sidebarExpanded = pinned || hovered;
  const location = useLocation();

  const logout = () => {
    sessionStorage.removeItem("radhAIAdminLogin");
    onLogout();
  };

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-slate-50 text-gray-900">
      {/* Subtle background */}
      <div className="pointer-events-none fixed inset-0 z-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_70%_50%_at_0%_0%,rgba(59,130,246,0.04),transparent)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_100%_100%,rgba(34,197,94,0.03),transparent_40%)]" />
      </div>

      {/* ── DESKTOP SIDEBAR ── */}
      <aside
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className={`fixed left-0 top-0 z-[100] hidden h-screen flex-col border-r-2 border-slate-200 bg-white shadow-xl transition-all duration-300 lg:flex ${
          sidebarExpanded ? "w-[260px]" : "w-[68px]"
        }`}
      >
        {/* Brand Header */}
        <div className="flex h-[64px] shrink-0 items-center border-b-2 border-slate-100 px-3">
          <div className="flex w-full items-center gap-3">
            {/* Logo icon — fixed 40×40, perfectly centered */}
            <div className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-600 via-sky-500 to-cyan-400 text-white shadow-lg shadow-blue-200">
              <Bot size={19} />
              <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500 shadow-sm">
                <Zap size={8} className="text-white" />
              </span>
            </div>

            {sidebarExpanded && (
              <>
                <div className="min-w-0 flex-1 overflow-hidden">
                  <h1 className="truncate text-[16px] font-black tracking-tight text-gray-900">
                    radhAI Admin
                  </h1>
                  <p className="truncate text-[12px] font-bold text-slate-600">
                    CEO Input Control
                  </p>
                </div>
                <span className="ml-auto flex shrink-0 items-center rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[9px] font-black text-emerald-700">
                  LIVE
                </span>
              </>
            )}
          </div>
        </div>

        {/* Modules label + pin toggle */}
        {sidebarExpanded && (
          <div className="flex items-center justify-between border-b-2 border-slate-200 bg-slate-50 px-4 py-2.5">
            <p className="text-[12px] font-black uppercase tracking-[0.2em] text-slate-700">
              Dashboard
            </p>
            <button
              onClick={() => setPinned((p) => !p)}
              title={pinned ? "Unpin sidebar" : "Pin sidebar open"}
              className={`flex h-9 w-9 items-center justify-center rounded-xl border-2 font-black transition-all ${
                pinned
                  ? "border-blue-600 bg-blue-600 text-white shadow-md"
                  : "border-slate-400 bg-white text-slate-700 hover:border-blue-500 hover:bg-blue-600 hover:text-white"
              }`}
            >
              {pinned ? <PinIcon size={15} /> : <ChevronRight size={15} />}
            </button>
          </div>
        )}

        {/* Nav items */}
        <nav className="flex-1 overflow-y-auto px-2 pb-3 pt-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
          <div className="space-y-0.5">
            {SIDEBAR_ITEMS.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) =>
                    `relative flex items-center rounded-xl border-2 transition-all duration-200 ${
                      sidebarExpanded
                        ? "gap-3 px-2 py-2"
                        : "justify-center px-0 py-2"
                    } ${
                      isActive
                        ? "shadow-sm"
                        : "border-transparent hover:border-slate-200 hover:bg-slate-50"
                    }`
                  }
                  style={({ isActive }) =>
                    isActive
                      ? {
                          backgroundColor: item.activeBg,
                          borderColor: item.activeBorder,
                        }
                      : {}
                  }
                >
                  {({ isActive }) => (
                    <>
                      {/* Icon container — fixed 36×36, perfectly centered */}
                      <span
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-all"
                        style={{
                          background: isActive ? item.accent : item.lightBg,
                          color: isActive ? "#fff" : item.accent,
                          boxShadow: isActive
                            ? `0 3px 10px ${item.accent}50`
                            : "none",
                        }}
                      >
                        <Icon size={16} />
                      </span>

                      {/* Labels — only when expanded */}
                      {sidebarExpanded && (
                        <div className="min-w-0 flex-1 overflow-hidden">
                          <span
                            className="block truncate text-[14px] font-black leading-tight"
                            style={{ color: isActive ? item.activeText : "#111827" }}
                          >
                            {item.title}
                          </span>
                          <span
                            className="mt-0.5 block truncate text-[12px] font-semibold leading-tight"
                            style={{ color: isActive ? item.accent : "#374151" }}
                          >
                            {item.subtitle}
                          </span>
                        </div>
                      )}

                      {/* Active dot — inside the row, right edge */}
                      {isActive && sidebarExpanded && (
                        <span
                          className="ml-auto h-2 w-2 shrink-0 rounded-full"
                          style={{ background: item.accent }}
                        />
                      )}

                      {/* Active dot — collapsed mode, right edge of sidebar */}
                      {isActive && !sidebarExpanded && (
                        <span
                          className="absolute right-1.5 top-1/2 h-1.5 w-1.5 -translate-y-1/2 rounded-full"
                          style={{ background: item.accent }}
                        />
                      )}
                    </>
                  )}
                </NavLink>
              );
            })}
          </div>
        </nav>

        {/* Bottom: user info + logout */}
        <div className="shrink-0 space-y-2 border-t-2 border-slate-100 p-2">
          {/* User row */}
          <div
            className={`flex items-center rounded-xl border-2 border-slate-100 bg-slate-50 ${
              sidebarExpanded ? "gap-2.5 px-2.5 py-2" : "justify-center px-0 py-2"
            }`}
          >
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-100">
              <User size={13} className="text-blue-700" />
            </div>
            {sidebarExpanded && (
              <div className="min-w-0 flex-1 overflow-hidden">
                <p className="truncate text-[13px] font-black text-gray-900">
                  radhAI.clone
                </p>
                <p className="truncate text-[11px] font-semibold text-slate-600">
                  CEO · Super Admin
                </p>
              </div>
            )}
            <span className="h-2 w-2 shrink-0 rounded-full bg-emerald-500 shadow-sm" />
          </div>

          {/* Logout button */}
          <button
            onClick={logout}
            className={`flex w-full items-center justify-center rounded-xl border-2 border-red-200 bg-red-50 py-2 text-xs font-black text-red-700 transition hover:border-red-300 hover:bg-red-100 ${
              sidebarExpanded ? "gap-2 px-3" : "gap-0 px-0"
            }`}
          >
            <LogOut size={14} />
            {sidebarExpanded && <span>Sign Out</span>}
          </button>
        </div>
      </aside>

      {/* ── MOBILE TOPBAR ── */}
      <header
        className="sticky top-0 z-[90] flex h-14 items-center justify-between border-b-2 border-slate-200 bg-white px-4 shadow-sm lg:hidden"
        style={{ WebkitTapHighlightColor: "transparent" }}
      >
       <div className="flex items-center gap-2.5">
  <button
    onClick={() => setMobileOpen((v) => !v)}
    aria-label="Toggle Menu"
    className="flex h-10 w-10 items-center justify-center rounded-xl border-2 border-slate-300 bg-slate-100 text-gray-700"
  >
    {mobileOpen ? <X size={18} /> : <Menu size={18} />}
  </button>

  <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-blue-600 to-cyan-500 text-white shadow-md shadow-blue-200">
    <LayoutDashboard size={17} />
  </div>

  <div>
    <h1 className="text-[16px] font-black leading-tight text-gray-900">
      radhAI Admin
    </h1>
    <p className="text-[12px] font-bold leading-tight text-slate-600">
      CEO Control
    </p>
  </div>
</div>

        <div className="flex items-center gap-2">
          <button
            onClick={logout}
            aria-label="Sign Out"
            className="flex h-10 w-10 items-center justify-center rounded-xl border-2 border-red-300 bg-red-50 text-red-600 transition active:scale-95 hover:bg-red-100"
            style={{ WebkitTapHighlightColor: "transparent" }}
          >
            <LogOut size={18} />
          </button>
        
        </div>
      </header>

      {/* ── MOBILE DRAWER ── */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[80] lg:hidden">
          <div
            className="absolute inset-0 bg-black/30 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />
          <nav
className="absolute bottom-0 left-0 top-14 flex w-[280px] flex-col border-r-2 border-slate-200 bg-white shadow-2xl"            style={{ WebkitOverflowScrolling: "touch" }}
          >
            <div className="border-b-2 border-slate-200 bg-slate-50 px-5 py-3">
              <p className="text-[12px] font-black uppercase tracking-[0.2em] text-slate-700">
                Navigation
              </p>
            </div>

            <div
              className="flex-1 space-y-1 overflow-y-auto px-3 py-3"
              style={{ WebkitOverflowScrolling: "touch" }}
            >
              {SIDEBAR_ITEMS.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 rounded-xl border-2 px-3 py-3.5 transition-all active:scale-[0.98] ${
                        isActive
                          ? "shadow-sm"
                          : "border-transparent hover:border-slate-200 hover:bg-slate-50"
                      }`
                    }
                    style={({ isActive }) =>
                      isActive
                        ? {
                            backgroundColor: item.activeBg,
                            borderColor: item.activeBorder,
                          }
                        : {}
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <span
                          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
                          style={{
                            background: isActive ? item.accent : item.lightBg,
                            color: isActive ? "#fff" : item.accent,
                          }}
                        >
                          <Icon size={16} />
                        </span>
                        <div className="min-w-0 flex-1 overflow-hidden">
                          <span
                            className="block truncate text-[15px] font-black leading-tight"
                            style={{ color: isActive ? item.activeText : "#111827" }}
                          >
                            {item.title}
                          </span>
                          <span
                            className="mt-0.5 block truncate text-[12px] font-semibold leading-tight"
                            style={{ color: isActive ? item.accent : "#374151" }}
                          >
                            {item.subtitle}
                          </span>
                        </div>
                        {isActive && (
                          <span
                            className="ml-auto h-2 w-2 shrink-0 rounded-full"
                            style={{ background: item.accent }}
                          />
                        )}
                      </>
                    )}
                  </NavLink>
                );
              })}
            </div>

            <div className="shrink-0 space-y-2 border-t-2 border-slate-100 p-3">
              <div className="flex items-center gap-2.5 rounded-xl border-2 border-slate-100 bg-slate-50 px-3 py-2.5">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-100">
                  <User size={15} className="text-blue-700" />
                </div>
                <div className="min-w-0 flex-1 overflow-hidden">
                  <p className="truncate text-[14px] font-black text-gray-900">
                    radhAI.clone
                  </p>
                  <p className="truncate text-[12px] font-semibold text-slate-600">
                    CEO · Super Admin
                  </p>
                </div>
                <span className="h-2 w-2 shrink-0 rounded-full bg-emerald-500" />
              </div>
              <button
                onClick={logout}
                className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-red-300 bg-red-50 px-3 py-3 text-[14px] font-black text-red-700 transition hover:bg-red-100 active:scale-95"
                style={{ WebkitTapHighlightColor: "transparent" }}
              >
                <LogOut size={16} />
                Sign Out
              </button>
            </div>
          </nav>
        </div>
      )}

      {/* ── MAIN CONTENT ── */}
      <main
        className={`relative z-10 min-h-screen transition-all duration-300 ${
          sidebarExpanded ? "lg:pl-[260px]" : "lg:pl-[68px]"
        }`}
      >
        <Outlet />
      </main>
    </div>
  );
}
